import { type ForwardRefExoticComponent, forwardRef } from 'react';

import { useSlotContent } from '../../hooks/use-slot-content';
import { CardBlockAnchor } from './card-block-anchor';
import { CardBody } from './card-body';
import { CardContent } from './card-content';
import { CardFooter } from './card-footer';
import { CardHeader } from './card-header';
import { CardRoot } from './card-root';
import type { CardPropsInterface } from './types/card.types';

/**
 * @since 4.1.0
 */
export const Card = forwardRef<HTMLDivElement, CardPropsInterface>(
  (props: CardPropsInterface, forwardedRef) => {
    const {
      asChild = false,
      blockAnchorUrl,
      body,
      children,
      dividers = true,
      footer,
      header,
      ...rest
    } = props;

    const content = useSlotContent({
      children,
      asChild,
      template: (childContent) => {
        const innerContent = (
          <>
            {header ? <Card.Header divider={dividers}>{header}</Card.Header> : null}
            {body ? <Card.Body>{body}</Card.Body> : null}
            {childContent}
            {footer ? <Card.Footer divider={dividers}>{footer}</Card.Footer> : null}
          </>
        );

        return blockAnchorUrl ? (
          <>
            <Card.BlockAnchor href={blockAnchorUrl} />
            <Card.Content>{innerContent}</Card.Content>
          </>
        ) : (
          innerContent
        );
      },
      dependencies: [header, body, footer, dividers, blockAnchorUrl],
    });

    return (
      <Card.Root ref={forwardedRef} asChild={asChild} {...rest}>
        {content}
      </Card.Root>
    );
  },
) as ForwardRefExoticComponent<CardPropsInterface> & {
  Root: typeof CardRoot;
  BlockAnchor: typeof CardBlockAnchor;
  Content: typeof CardContent;
  Header: typeof CardHeader;
  Body: typeof CardBody;
  Footer: typeof CardFooter;
};

Card.displayName = 'Card';

Card.Root = CardRoot;
Card.BlockAnchor = CardBlockAnchor;
Card.Content = CardContent;
Card.Header = CardHeader;
Card.Body = CardBody;
Card.Footer = CardFooter;
